import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Export, DownloadSimple } from '@phosphor-icons/react';
import { isIos, isStandalone } from '@/lib/pwa';
import { trackPwa } from '@/lib/pwaTrack';

/**
 * Mobile-web PWA install nudge.
 *
 * A slim card that docks just above the mobile bottom nav and invites users who
 * haven't installed the app to do so. It appears once the user has spent ~30s
 * on the site and only ever once per browser session:
 *  - `nos_pwa_banner_dwell_ms` accumulates active time so a refresh or route
 *    change continues the 30s clock instead of resetting it.
 *  - `nos_pwa_banner_shown` is set the moment it appears, so a refresh /
 *    navigation / dismiss won't bring it back this session. A brand-new session
 *    (fresh tab or browser restart) clears sessionStorage and it can show again.
 *
 * Never shown to installed users (standalone display mode) or on desktop.
 * On Android/desktop the CTA fires the captured native install prompt; on iOS
 * (no programmatic install) it expands to the Add-to-Home-Screen steps.
 */

const SHOWN_KEY = 'nos_pwa_banner_shown';
const DWELL_KEY = 'nos_pwa_banner_dwell_ms';
const DWELL_TARGET_MS = 30000;
const TICK_MS = 1000;

const isMobileViewport = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(max-width: 767px)').matches;

const PwaInstallBanner = () => {
  const [visible, setVisible] = useState(false);
  const [entered, setEntered] = useState(false); // drives the slide-up transition
  const [installable, setInstallable] = useState(
    typeof window !== 'undefined' && !!window.__deferredInstallPrompt
  );
  const [showSteps, setShowSteps] = useState(false);
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    setEntered(false); // slide down…
    setTimeout(() => setVisible(false), 260); // …then unmount
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    // Installed app, desktop, or already shown this session -> never show.
    const ineligible = () =>
      isStandalone() ||
      !isMobileViewport() ||
      sessionStorage.getItem(SHOWN_KEY) === '1';

    if (ineligible()) return undefined;

    const reveal = () => {
      if (ineligible()) return;
      sessionStorage.setItem(SHOWN_KEY, '1');
      trackPwa('pwa_banner_impression', { platform: isIos() ? 'ios' : 'other' });
      setVisible(true);
      // Mount first, then flip `entered` a tick later so the transition runs.
      // setTimeout (not rAF) so it still fires if the tab is briefly backgrounded.
      setTimeout(() => setEntered(true), 30);
    };

    let dwell = parseInt(sessionStorage.getItem(DWELL_KEY) || '0', 10) || 0;
    if (dwell >= DWELL_TARGET_MS) {
      reveal();
    } else {
      timerRef.current = setInterval(() => {
        if (document.hidden) return; // only count time the tab is actually visible
        dwell += TICK_MS;
        sessionStorage.setItem(DWELL_KEY, String(dwell));
        if (dwell >= DWELL_TARGET_MS) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          reveal();
        }
      }, TICK_MS);
    }

    const onInstallable = () => setInstallable(true);
    const onInstalled = () => dismiss();
    window.addEventListener('pwa-installable', onInstallable);
    window.addEventListener('pwa-installed', onInstalled);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('pwa-installable', onInstallable);
      window.removeEventListener('pwa-installed', onInstalled);
    };
  }, [dismiss]);

  const handleInstall = async () => {
    const dp = window.__deferredInstallPrompt;
    if (dp) {
      trackPwa('pwa_banner_install_click', { install_method: 'native_prompt' });
      dp.prompt();
      try {
        const choice = await dp.userChoice;
        trackPwa(
          choice?.outcome === 'accepted'
            ? 'pwa_banner_install_accepted'
            : 'pwa_banner_install_dismissed'
        );
      } finally {
        window.__deferredInstallPrompt = null;
        setInstallable(false);
        dismiss(); // whatever the choice, the banner has done its job this session
      }
      return;
    }
    // iOS / browsers without a captured prompt: reveal the manual steps inline.
    trackPwa('pwa_banner_install_click', {
      install_method: isIos() ? 'ios_manual' : 'manual_steps',
    });
    setShowSteps((s) => {
      const next = !s;
      if (next) trackPwa('pwa_banner_ios_steps_shown', { platform: isIos() ? 'ios' : 'other' });
      return next;
    });
  };

  // The X / cross button. Kept separate from `dismiss` so the auto-close on a
  // successful install doesn't fire a "close" event.
  const handleClose = () => {
    trackPwa('pwa_banner_close');
    dismiss();
  };

  if (!visible) return null;

  const ios = isIos();

  return createPortal(
    <div
      className="md:hidden fixed left-0 right-0 z-[60] px-3 pointer-events-none"
      style={{
        bottom: 'calc(64px + env(safe-area-inset-bottom) + 8px)',
        transform: entered ? 'translateY(0)' : 'translateY(140%)',
        opacity: entered ? 1 : 0,
        transition: 'transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 260ms ease',
      }}
      data-testid="pwa-install-banner"
      role="dialog"
      aria-label="Install the North of Six app"
    >
      <div className="pointer-events-auto mx-auto max-w-md bg-white border border-[#E4E1D9] rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] overflow-hidden">
        <div className="flex items-center gap-3 p-3">
          <img
            src="/icons/icon-192.png"
            alt=""
            aria-hidden="true"
            className="w-11 h-11 rounded-xl border border-[#E4E1D9] flex-shrink-0"
          />

          <div className="min-w-0 flex-1">
            <p
              className="text-[17px] leading-tight text-[#2D302A] tracking-tight whitespace-nowrap"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Get the app
            </p>
            <p className="text-[11px] text-[#6B6A68] mt-0.5 leading-snug">
              Full-screen, on your home screen.
            </p>
          </div>

          <button
            onClick={handleInstall}
            className="flex-shrink-0 inline-flex items-center gap-1.5 bg-[#2D302A] text-white px-3.5 py-2 rounded-full hover:bg-[#1A1C18] transition-colors"
            style={{ fontFamily: 'IBM Plex Mono, monospace' }}
            data-testid="pwa-banner-install"
          >
            <DownloadSimple size={15} weight="bold" />
            <span className="uppercase tracking-[0.12em] text-[10px] whitespace-nowrap">
              Install
            </span>
          </button>

          <button
            onClick={handleClose}
            className="flex-shrink-0 -mr-1 p-1 text-[#9A9890] hover:text-[#2D302A] transition-colors"
            aria-label="Dismiss"
            data-testid="pwa-banner-close"
          >
            <X size={18} />
          </button>
        </div>

        {showSteps && (
          <div className="border-t border-[#EEEBE3] px-4 py-3 bg-[#F9F8F6]">
            {ios ? (
              <ol className="text-[12px] text-[#4A4A48] space-y-1.5 list-decimal list-inside">
                <li>
                  Tap the Share icon{' '}
                  <Export size={15} className="inline align-text-bottom" weight="bold" />{' '}
                  in Safari's toolbar
                </li>
                <li>
                  Scroll down and tap <b>Add to Home Screen</b>
                </li>
                <li>Open North of Six from your Home Screen</li>
              </ol>
            ) : (
              <p className="text-[12px] text-[#4A4A48]">
                Open your browser menu and choose <b>Install</b> or{' '}
                <b>Add to Home Screen</b>.
              </p>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default PwaInstallBanner;
